import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import {
  get_job_postings_api,
  create_job_posting_api,
  update_job_posting_api,
} from "../../DAL/Careers/Careers";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "9px",
    fontFamily: "'Rubik', sans-serif",
    "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontFamily: "'Rubik', sans-serif", color: "#aaa" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
};

const EMPTY = {
  title: "",
  slug: "",
  department: "",
  location: "Remote (UK)",
  employmentType: "full-time",
  salaryMin: "",
  salaryMax: "",
  salaryNote: "",
  summary: "",
  description: "",
  responsibilities: [],
  requirements: [],
  status: "draft",
  displayOrder: 0,
};

const slugify = (str) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export default function AddOrUpdateJobPosting() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { job_id } = useParams();
  const isEdit = Boolean(job_id);

  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) loadJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadJob = async () => {
    try {
      const result = await get_job_postings_api();
      if (result.code === 200) {
        const job = (result.data || []).find((j) => j._id === job_id);
        if (job) {
          setForm({
            title: job.title || "",
            slug: job.slug || "",
            department: job.department || "",
            location: job.location || "",
            employmentType: job.employmentType || "full-time",
            salaryMin: job.salaryMin ?? "",
            salaryMax: job.salaryMax ?? "",
            salaryNote: job.salaryNote || "",
            summary: job.summary || "",
            description: job.description || "",
            responsibilities: job.responsibilities || [],
            requirements: job.requirements || [],
            status: job.status || "draft",
            displayOrder: job.displayOrder || 0,
          });
          setSlugTouched(true);
        } else {
          enqueueSnackbar("Job posting not found", { variant: "error" });
        }
      }
    } catch (error) {
      enqueueSnackbar("Error loading job posting", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "title" && !slugTouched) next.slug = slugify(value);
      return next;
    });
  };

  const updateListItem = (key, index, value) => {
    setForm((prev) => {
      const arr = [...prev[key]];
      arr[index] = value;
      return { ...prev, [key]: arr };
    });
  };
  const addListItem = (key) => setForm((prev) => ({ ...prev, [key]: [...prev[key], ""] }));
  const removeListItem = (key, index) =>
    setForm((prev) => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }));

  const handleSave = async () => {
    if (!form.title || !form.slug || !form.department || !form.summary || !form.description || form.salaryMin === "" || form.salaryMax === "") {
      enqueueSnackbar("Please fill in all required fields", { variant: "error" });
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      salaryMin: Number(form.salaryMin),
      salaryMax: Number(form.salaryMax),
      responsibilities: form.responsibilities.filter((r) => r.trim()),
      requirements: form.requirements.filter((r) => r.trim()),
    };
    const result = isEdit
      ? await update_job_posting_api(job_id, payload)
      : await create_job_posting_api(payload);

    if (result.code === 200) {
      enqueueSnackbar(isEdit ? "Job posting updated" : "Job posting created", { variant: "success" });
      navigate("/careers/jobs");
    } else {
      enqueueSnackbar(result.message || "Failed to save job posting", { variant: "error" });
    }
    setSaving(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress sx={{ color: "#c01e2e" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: "calc(100vh - 48px)" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
        <Tooltip title="Back to Job Postings">
          <IconButton onClick={() => navigate("/careers/jobs")} sx={{ color: "#6b6b6b" }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>
          {isEdit ? "Edit Job Posting" : "Add Job Posting"}
        </Typography>
      </Box>

      <Paper sx={{ backgroundColor: "#ffffff", border: "1px solid rgba(26,26,26,0.09)", borderRadius: "14px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", p: 3 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
          <TextField label="Title" required value={form.title} onChange={(e) => updateField("title", e.target.value)} sx={fieldSx} size="small" />
          <TextField
            label="Slug"
            required
            value={form.slug}
            onChange={(e) => { setSlugTouched(true); updateField("slug", slugify(e.target.value)); }}
            sx={fieldSx}
            size="small"
            helperText="Used in the public apply URL"
          />
          <TextField label="Department" required value={form.department} onChange={(e) => updateField("department", e.target.value)} sx={fieldSx} size="small" />
          <TextField label="Location" value={form.location} onChange={(e) => updateField("location", e.target.value)} sx={fieldSx} size="small" />
          <TextField select label="Employment Type" value={form.employmentType} onChange={(e) => updateField("employmentType", e.target.value)} sx={fieldSx} size="small">
            <MenuItem value="full-time">Full-time</MenuItem>
            <MenuItem value="part-time">Part-time</MenuItem>
            <MenuItem value="contract">Contract</MenuItem>
          </TextField>
          <TextField select label="Status" value={form.status} onChange={(e) => updateField("status", e.target.value)} sx={fieldSx} size="small">
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </TextField>
          <TextField label="Salary Min (£)" required type="number" value={form.salaryMin} onChange={(e) => updateField("salaryMin", e.target.value)} sx={fieldSx} size="small" />
          <TextField label="Salary Max (£)" required type="number" value={form.salaryMax} onChange={(e) => updateField("salaryMax", e.target.value)} sx={fieldSx} size="small" />
          <TextField label="Salary Note" placeholder="e.g. + commission" value={form.salaryNote} onChange={(e) => updateField("salaryNote", e.target.value)} sx={fieldSx} size="small" />
          <TextField label="Display Order" type="number" value={form.displayOrder} onChange={(e) => updateField("displayOrder", e.target.value)} sx={fieldSx} size="small" />
        </Box>

        <TextField
          label="Summary"
          required
          fullWidth
          multiline
          minRows={2}
          value={form.summary}
          onChange={(e) => updateField("summary", e.target.value)}
          sx={{ ...fieldSx, mb: 2 }}
          size="small"
          helperText="Short blurb shown on the role card"
        />

        <TextField
          label="Full Description"
          required
          fullWidth
          multiline
          minRows={5}
          value={form.description}
          onChange={(e) => updateField("description", e.target.value)}
          sx={{ ...fieldSx, mb: 3 }}
          size="small"
        />

        {["responsibilities", "requirements"].map((key) => (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 1, textTransform: "capitalize" }}>
              {key}
            </Typography>
            {form[key].map((item, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField fullWidth size="small" value={item} onChange={(e) => updateListItem(key, i, e.target.value)} sx={fieldSx} />
                <IconButton size="small" onClick={() => removeListItem(key, i)} sx={{ color: "#aaa", "&:hover": { color: "#c01e2e" } }}>
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => addListItem(key)}
              sx={{ textTransform: "none", color: "#6b6b6b", fontFamily: "'Rubik', sans-serif", fontSize: "13px" }}
            >
              Add {key === "responsibilities" ? "responsibility" : "requirement"}
            </Button>
          </Box>
        ))}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 2, borderTop: "1px solid rgba(26,26,26,0.09)" }}>
          <Button onClick={() => navigate("/careers/jobs")} sx={{ textTransform: "none", color: "#6b6b6b", fontFamily: "'Rubik', sans-serif" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            sx={{ backgroundColor: "#c01e2e", color: "#fff", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px", px: 3, boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Job Posting"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}